/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import ExamTimer from './ExamTimer';
import UsageTracker from '../../../utils/UsageTracker';
import { useMostRecentExamAttempt, useResumeExam, useValidateExam } from '../examsQueries';
import { useFullTrack } from '../../tracks';
import { BaseContentControlWrapper } from '../../../components/ContentControlWrapper';
import { Metrics } from '../../../Metrics';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ContentWrapper = styled.div.withConfig({
  displayName: "ExamResumeScreen__ContentWrapper"
})(({
  theme
}) => ({
  backgroundColor: theme.color['fill-surface-recessed'],
  padding: '10% 5%'
}));
const ExamResumeScreen = ({
  trackId,
  language
}) => {
  const {
    mostRecentExamAttempt
  } = useMostRecentExamAttempt(Number(trackId), language);
  const {
    track
  } = useFullTrack(trackId, language);
  const [validateExam, {
    validateExamLoading,
    validateExamError
  }] = useValidateExam(Number(trackId), language);
  const [resumeExam, {
    resumeExamLoading,
    resumeExamError
  }] = useResumeExam();
  const totalQuestionCount = track && track.exam ? track.exam.length : 0;
  const answeredQuestionCount = mostRecentExamAttempt ? mostRecentExamAttempt.questionSubmissions.length : 0;
  const remainingQuestionCount = totalQuestionCount - answeredQuestionCount;
  const handleResume = useCallback(() => {
    if (!mostRecentExamAttempt) return null;
    if (answeredQuestionCount >= totalQuestionCount) {
      return validateExam(mostRecentExamAttempt.id).then(() => {
        UsageTracker.track('examInteraction', {
          action: 'completed',
          examId: track && track.exam.id,
          examLength: mostRecentExamAttempt.questions.length,
          trackId: trackId && trackId.toString(),
          trackName: track && track.name,
          examAttemptId: mostRecentExamAttempt.id && mostRecentExamAttempt.id.toString(),
          language: mostRecentExamAttempt.language
        });
        Metrics.counter('exam-completed', {
          language: mostRecentExamAttempt.language
        }).increment();
      });
    }
    UsageTracker.track('examInteraction', {
      action: 'resume',
      examId: track && track.exam.id,
      examLength: mostRecentExamAttempt.questions.length,
      trackId: trackId && trackId.toString(),
      trackName: track && track.name,
      examAttemptId: mostRecentExamAttempt.id && mostRecentExamAttempt.id.toString()
    });
    return resumeExam(Number(trackId), mostRecentExamAttempt.id).then(() => {
      Metrics.counter('exam-resumed', {
        language: mostRecentExamAttempt.language
      }).increment();
    });
  }, [answeredQuestionCount, mostRecentExamAttempt, resumeExam, totalQuestionCount, track, trackId, validateExam]);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(ContentWrapper, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        blockSize: "100%",
        flexDirection: "row",
        justifyContent: "center",
        children: [/*#__PURE__*/_jsx(Illustration, {
          marginBlockStart: "500",
          marginInlineEnd: "800",
          name: "nps-question",
          width: 150
        }), /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.exams.resume.heading"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.exams.resume.remainingQuestionCount",
            options: {
              count: remainingQuestionCount
            }
          }), /*#__PURE__*/_jsx(ExamTimer, {
            language: language,
            trackId: trackId
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(BaseContentControlWrapper, {
      children: /*#__PURE__*/_jsx(LoadingButton, {
        failed: !!validateExamError || !!resumeExamError,
        loading: validateExamLoading || resumeExamLoading,
        onClick: handleResume,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.resume.resumeExam"
        })
      })
    })]
  });
};
export default ExamResumeScreen;